// Workflow completion service for storing transaction results and schedule data
// Enables linking between schedule and execute workflows

import logger from '../utils/logger';
import { backendDataHandler } from './backendDataHandler';
import { workflowStorageService, WorkflowData } from './workflowStorageService';
import { computeOperationId } from '../utils/timelockDecoder';

export interface ScheduleData {
    target: string;
    value: string;
    data: string; // KV reference to original function
    predecessor: string;
    salt: string;
    delay: string;
    operationHash?: string;
}

// REMOVED: EnhancedScheduleParameters interface - using simple scheduleParameters instead

export interface WorkflowCompletionData {
    workflowId: string;
    workflowName: string;
    transactionHash: string;
    blockNumber: number;
    gasUsed: string;
    scheduleData: ScheduleData;
    // REMOVED: enhancedScheduleParameters - using simple scheduleParameters instead
    executionTimestamp: number;
    status: '' | 'scheduled' | 'completed' | 'failed' | 'pending' | 'ready' | 'executed' | 'building_execute'; // FIXED: Added empty string as initial status
    multiSigAddress: string;
    functionName: string;
    originalFunctionData: string; // KV reference from Step 1
    // PHASE 2: New fields for enhanced tracking
    operationId: string; // FIXED: Required field, computed from schedule parameters
    lastStatusCheck?: number; // Timestamp of last status update
    kvReferences?: string[]; // Array of related KV record IDs
    // CRITICAL FIX: Add tier1 property for frontend filtering compatibility
    tier1?: any; // Preserve original tier1 structure from backend
    debugInfo?: {
        saltCaptureMethod?: string;
        parameterSources?: { [key: string]: string };
        kvReferences?: string[];
        workflowSteps?: string[];
        scheduleParametersIncluded?: boolean;
        operationIdExtractionMethod?: string; // PHASE 2: How operationId was obtained
        willUpdateAfterTransaction?: boolean; // UNIVERSAL: Indicates record will be updated after transaction
        originalScheduleOperationId?: string; // UNIVERSAL: Links execute workflow to original schedule
        extractionMethod?: string; // Method used to extract data from workflow record
        originalWorkflowKeys?: string[]; // Keys from original workflow record
        hasTemplateData?: boolean; // Whether templateData exists
        hasFunctionData?: boolean; // Whether functionData exists
        functionDataType?: string; // Type of functionData
        scheduleParametersSummary?: {
            targetSource?: string;
            saltSource?: string;
            dataSource?: string;
            saltValue?: string;
            saltDescription?: string;
        };
        // CRITICAL DEBUG: Additional debug fields for tier1 preservation
        originalTier1Exists?: boolean;
        originalTier1Status?: string;
        finalTier1Exists?: boolean;
        finalStatus?: string;
        statusSource?: string;
    };
}

export interface WorkflowCompletionResponse {
    success: boolean;
    id?: string;
    error?: string;
}

class WorkflowCompletionService {
    private readonly COMPLETION_PREFIX = 'workflow_completion_';

    /**
     * Direct API update - NOW WITH AUTH0 SECURITY
     */
    async updateWorkflowCompletionDirect(
        completionId: string,
        fieldUpdates: { [key: string]: any }
    ): Promise<WorkflowCompletionResponse> {
        try {
            logger.log('🔄 DIRECT UPDATE: Updating workflow completion fields:', completionId, fieldUpdates);

            // SECURITY FIX: Use workflowStorageService which has Auth0 integration
            const result = await workflowStorageService.updateWorkflow(completionId, fieldUpdates);

            if (!result.success) {
                throw new Error(result.error || 'Update failed');
            }

            return {
                success: true,
                id: completionId
            };

        } catch (error) {
            logger.error('❌ Direct update failed:', error);
            return {
                success: false,
                error: `Direct update failed: ${error instanceof Error ? error.message : String(error)}`
            };
        }
    }

    /**
     * Update workflow completion with transaction results (REPLACED - UNIVERSAL FIX)
     */
    async updateWorkflowCompletion(
        completionId: string,
        updatedData: WorkflowCompletionData
    ): Promise<WorkflowCompletionResponse> {
        try {
            logger.log('🔄 PHASE 4.2: Direct field update for workflow completion:', completionId);

            // Create field updates object with only changed fields
            // CRITICAL FIX: Update tier1 fields directly, not templateData nested fields
            const fieldUpdates = {
                'transactionHash': updatedData.transactionHash,
                'status': updatedData.status,
                'lastStatusCheck': Date.now(),
                'executionTimestamp': updatedData.executionTimestamp,
                'multiSigAddress': updatedData.multiSigAddress,
                'blockNumber': updatedData.blockNumber,
                'gasUsed': updatedData.gasUsed
            };

            return await this.updateWorkflowCompletionDirect(completionId, fieldUpdates);

        } catch (error) {
            logger.error('❌ PHASE 4.2: Failed to update workflow completion:', error);
            return {
                success: false,
                error: `Update failed: ${error instanceof Error ? error.message : String(error)}`
            };
        }
    }

    /**
     * Compute operation hash for timelock operation
     */
    private computeOperationHash(scheduleData: ScheduleData): string {
        // This would normally use the same hashing algorithm as the timelock contract
        // For now, return a placeholder that could be computed client-side
        return `0x${scheduleData.target.slice(2)}${scheduleData.salt.slice(2)}`.substring(0, 66);
    }

    /**
     * Compute execution time based on schedule data
     */
    private computeExecutionTime(scheduleData: ScheduleData): string {
        const delaySeconds = parseInt(scheduleData.delay);
        const executionTime = new Date(Date.now() + (delaySeconds * 1000));
        return executionTime.toISOString();
    }

    /**
     * Retrieve workflow completion data by workflow ID
     */
    async getWorkflowCompletion(workflowId: string): Promise<WorkflowCompletionData | null> {
        try {
            logger.log('📥 Retrieving workflow completion data for:', workflowId);

            // First try sessionStorage for immediate access
            const sessionData = sessionStorage.getItem(`completion_${workflowId}`);
            if (sessionData) {
                logger.log('✅ Found completion data in sessionStorage');
                return JSON.parse(sessionData);
            }

            // Try backend retrieval
            const completionId = `${this.COMPLETION_PREFIX}${workflowId}`;
            const response = await backendDataHandler.retrieveEncodedData(completionId, 'workflow');

            if (response.success && response.data) {
                logger.log('✅ Retrieved completion data from backend');
                return response.data as WorkflowCompletionData;
            }

            logger.log('⚠️ No completion data found for workflow:', workflowId);
            return null;

        } catch (error) {
            logger.error('❌ Failed to retrieve workflow completion:', error);
            return null;
        }
    }

    /**
     * Get schedule data for execute workflow
     */
    async getScheduleDataForExecute(workflowId: string): Promise<ScheduleData | null> {
        try {
            const completionData = await this.getWorkflowCompletion(workflowId);

            if (completionData && completionData.scheduleData) {
                logger.log('✅ Retrieved schedule data for execute workflow:', {
                    target: completionData.scheduleData.target,
                    salt: completionData.scheduleData.salt,
                    delay: completionData.scheduleData.delay
                });
                return completionData.scheduleData;
            }

            return null;
        } catch (error) {
            logger.error('❌ Failed to get schedule data for execute:', error);
            return null;
        }
    }

    /**
     * List all completed workflows - FIXED to read from backend storage
     */
    async listCompletedWorkflows(): Promise<WorkflowCompletionData[]> {
        try {
            const completedWorkflows: WorkflowCompletionData[] = [];

            logger.log('📋 Loading completed workflows from backend and sessionStorage...');

            // CRITICAL FIX: First try to load from backend storage
            try {
                const backendWorkflows = await this.loadWorkflowsFromBackend();
                completedWorkflows.push(...backendWorkflows);
                logger.log('✅ Loaded from backend:', backendWorkflows.length, 'workflows');
            } catch (backendError) {
                logger.warn('⚠️ Backend loading failed, continuing with sessionStorage only:', backendError);
            }

            // Then supplement with sessionStorage data (for recent completions not yet synced)
            const sessionKeys = Object.keys(sessionStorage);
            const completionKeys = sessionKeys.filter(key =>
                key.startsWith('completion_') || key.startsWith('fallback_completion_')
            );

            for (const key of completionKeys) {
                try {
                    const data = sessionStorage.getItem(key);
                    if (data) {
                        const parsed = JSON.parse(data);

                        // Avoid duplicates - check if this workflow is already in the backend results
                        const isDuplicate = completedWorkflows.some(existing =>
                            existing.workflowId === parsed.workflowId &&
                            existing.transactionHash === parsed.transactionHash
                        );

                        if (!isDuplicate) {
                            completedWorkflows.push(parsed);
                            logger.log('✅ Added unique sessionStorage workflow:', parsed.workflowName);
                        }
                    }
                } catch (parseError) {
                    logger.warn('Failed to parse completion data:', key, parseError);
                }
            }

            // Sort by execution timestamp (newest first)
            completedWorkflows.sort((a, b) => b.executionTimestamp - a.executionTimestamp);

            logger.log('📋 Total completed workflows found:', completedWorkflows.length);
            logger.log('🔍 Workflow breakdown:', completedWorkflows.map(w => ({
                name: w.workflowName,
                txHash: w.transactionHash?.substring(0, 10) + '...',
                timestamp: new Date(w.executionTimestamp).toLocaleString()
            })));

            return completedWorkflows;

        } catch (error) {
            logger.error('❌ Failed to list completed workflows:', error);
            return [];
        }
    }

    /**
     * Load workflows from backend storage
     */
    private async loadWorkflowsFromBackend(): Promise<WorkflowCompletionData[]> {
        try {
            logger.log('🔍 Attempting to load workflows from backend...');

            // CRITICAL FIX: Use workflowStorageService.listWorkflows() directly
            const workflowListResponse = await workflowStorageService.listWorkflows();

            if (workflowListResponse.success && workflowListResponse.data) {
                const workflows: WorkflowCompletionData[] = [];

                logger.log('🔍 DEBUG: Raw workflow list response:', workflowListResponse.data.length, 'items');

                // Filter for workflow completion entries
                const completionWorkflows = workflowListResponse.data.filter(w =>
                    w.name?.includes('Timelock') ||
                    w.workflowCompletionId ||
                    w.transactionHash ||
                    w.id?.includes('workflow_completion_')
                );

                logger.log('🔍 DEBUG: Filtered completion workflows:', completionWorkflows.length, 'items');
                completionWorkflows.forEach((w, i) => {
                    logger.log(`🔍 DEBUG: Completion workflow ${i}:`, {
                        id: w.id,
                        name: w.name,
                        hasTransactionHash: !!w.transactionHash,
                        hasScheduleParams: !!w.scheduleParameters,
                        keys: Object.keys(w)
                    });
                });

                for (const workflow of completionWorkflows) {
                    try {
                        logger.log('🔄 DEBUG: Converting workflow:', workflow.id);
                        logger.log('🔍 DEBUG: Workflow structure:', {
                            id: workflow.id,
                            name: workflow.name,
                            transactionHash: workflow.transactionHash,
                            scheduleParameters: workflow.scheduleParameters,
                            functionData: workflow.functionData?.substring(0, 50) + '...',
                            templateData: workflow.templateData ? 'EXISTS' : 'MISSING',
                            templateDataKeys: workflow.templateData ? Object.keys(workflow.templateData) : [],
                            allKeys: Object.keys(workflow)
                        });

                        // ENHANCED: More comprehensive data extraction with better debugging
                        let scheduleData;
                        let operationId: string | undefined;
                        let status: string = ''; // Start with empty status, don't default to completed
                        let extractionMethod = 'unknown';

                        // Method 0: FIXED - Backend returns tier1 data FLATTENED at root level for workflow completion records
                        if (workflow.id?.includes('workflow_completion_') && workflow.scheduleParameters) {
                            logger.log('✅ DEBUG: Method 0 - Found FLATTENED tier1 data at root level (ACTUAL API STRUCTURE)');
                            const workflowAny = workflow as any; // Type assertion for additional fields
                            scheduleData = {
                                target: workflow.scheduleParameters.target || '',
                                value: workflow.scheduleParameters.value || '0',
                                data: workflow.scheduleParameters.data || '0x',
                                predecessor: workflow.scheduleParameters.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000',
                                salt: workflow.scheduleParameters.salt || '',
                                delay: workflow.scheduleParameters.delay || ''
                            };
                            operationId = workflowAny.operationId || '';
                            status = workflowAny.status || 'scheduled';
                            extractionMethod = 'api_flattened_tier1_data';

                            logger.log('🔍 DEBUG: Flattened tier1 extraction details:', {
                                hasOperationId: !!operationId,
                                operationId: operationId ? operationId.substring(0, 20) + '...' : 'EMPTY',
                                status: status,
                                saltValue: scheduleData.salt ? scheduleData.salt.substring(0, 20) + '...' : 'MISSING',
                                targetValue: scheduleData.target || 'MISSING',
                                workflowName: workflowAny.workflowName || 'MISSING',
                                transactionHash: workflowAny.transactionHash || 'MISSING'
                            });
                        }
                        // Method 0b: Legacy nested tier1 structure (if it exists)
                        else if ((workflow as any).tier1?.scheduleParameters) {
                            logger.log('✅ DEBUG: Method 0b - Found workflow.tier1.scheduleParameters (NESTED STRUCTURE)');
                            const tier1 = (workflow as any).tier1;
                            scheduleData = {
                                target: tier1.scheduleParameters.target || '',
                                value: tier1.scheduleParameters.value || '0',
                                data: tier1.scheduleParameters.data || '0x',
                                predecessor: tier1.scheduleParameters.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000',
                                salt: tier1.scheduleParameters.salt || '',
                                delay: tier1.scheduleParameters.delay || ''
                            };
                            operationId = tier1.operationId || '';
                            status = tier1.status || 'scheduled';
                            extractionMethod = 'api_nested_tier1_scheduleParameters';

                            logger.log('🔍 DEBUG: Nested tier1 extraction details:', {
                                hasOperationId: !!operationId,
                                operationId: operationId ? operationId.substring(0, 20) + '...' : 'EMPTY',
                                status: status,
                                saltValue: scheduleData.salt ? scheduleData.salt.substring(0, 20) + '...' : 'MISSING',
                                targetValue: scheduleData.target || 'MISSING',
                                workflowName: tier1.workflowName || 'MISSING',
                                transactionHash: tier1.transactionHash || 'MISSING'
                            });
                        }
                        // Method 1: Direct scheduleData property
                        else if ('scheduleData' in workflow && workflow.scheduleData) {
                            logger.log('✅ DEBUG: Method 1 - Found direct scheduleData');
                            scheduleData = (workflow as any).scheduleData;
                            operationId = (workflow as any).operationId;
                            status = (workflow as any).status;
                            extractionMethod = 'direct_scheduleData';
                        }
                        // Method 2: scheduleParameters at root level
                        else if (workflow.scheduleParameters) {
                            logger.log('✅ DEBUG: Method 2 - Found root scheduleParameters');
                            scheduleData = {
                                target: workflow.scheduleParameters.target || '',
                                value: workflow.scheduleParameters.value || '0',
                                data: workflow.scheduleParameters.data || '0x',
                                predecessor: workflow.scheduleParameters.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000',
                                salt: workflow.scheduleParameters.salt || '',
                                delay: workflow.scheduleParameters.delay || ''
                            };
                            extractionMethod = 'root_scheduleParameters';
                        }
                        // Method 3: templateData.scheduleParameters (our tier1 structure)
                        else if (workflow.templateData?.scheduleParameters) {
                            logger.log('✅ DEBUG: Method 3 - Found templateData.scheduleParameters (tier1)');
                            scheduleData = {
                                target: workflow.templateData.scheduleParameters.target || '',
                                value: workflow.templateData.scheduleParameters.value || '0',
                                data: workflow.templateData.scheduleParameters.data || '0x',
                                predecessor: workflow.templateData.scheduleParameters.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000',
                                salt: workflow.templateData.scheduleParameters.salt || '',
                                delay: workflow.templateData.scheduleParameters.delay || ''
                            };
                            operationId = workflow.templateData.operationId;
                            status = workflow.templateData.status;
                            extractionMethod = 'templateData_tier1';

                            logger.log('🔍 DEBUG: Tier1 extraction details:', {
                                hasOperationId: !!operationId,
                                operationId: operationId ? operationId.substring(0, 20) + '...' : 'MISSING',
                                status: status,
                                saltValue: scheduleData.salt ? scheduleData.salt.substring(0, 20) + '...' : 'MISSING',
                                targetValue: scheduleData.target || 'MISSING'
                            });
                        }
                        // Method 4: Parse functionData as JSON
                        else if (workflow.functionData && typeof workflow.functionData === 'string' && workflow.functionData !== 'undefined') {
                            logger.log('✅ DEBUG: Method 4 - Parsing functionData as JSON');
                            try {
                                const parsed = JSON.parse(workflow.functionData);
                                logger.log('🔍 DEBUG: Parsed functionData structure:', Object.keys(parsed));

                                if (parsed.scheduleData) {
                                    logger.log('✅ DEBUG: Found scheduleData in parsed functionData');
                                    scheduleData = parsed.scheduleData;
                                    operationId = parsed.operationId;
                                    status = parsed.status;
                                    extractionMethod = 'parsed_functionData_scheduleData';
                                } else if (parsed.scheduleParameters) {
                                    logger.log('✅ DEBUG: Found scheduleParameters in parsed functionData');
                                    scheduleData = {
                                        target: parsed.scheduleParameters.target || '',
                                        value: parsed.scheduleParameters.value || '0',
                                        data: parsed.scheduleParameters.data || '0x',
                                        predecessor: parsed.scheduleParameters.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000',
                                        salt: parsed.scheduleParameters.salt || '',
                                        delay: parsed.scheduleParameters.delay || ''
                                    };
                                    operationId = parsed.operationId;
                                    status = parsed.status;
                                    extractionMethod = 'parsed_functionData_scheduleParameters';
                                } else if (parsed.tier1?.scheduleParameters) {
                                    logger.log('✅ DEBUG: Found tier1.scheduleParameters in parsed functionData');
                                    const tier1 = parsed.tier1;
                                    scheduleData = {
                                        target: tier1.scheduleParameters.target || '',
                                        value: tier1.scheduleParameters.value || '0',
                                        data: tier1.scheduleParameters.data || '0x',
                                        predecessor: tier1.scheduleParameters.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000',
                                        salt: tier1.scheduleParameters.salt || '',
                                        delay: tier1.scheduleParameters.delay || ''
                                    };
                                    operationId = tier1.operationId;
                                    status = tier1.status;
                                    extractionMethod = 'parsed_functionData_tier1';
                                }
                            } catch (parseError) {
                                logger.warn('⚠️ DEBUG: Failed to parse functionData as JSON:', parseError);
                                extractionMethod = 'parse_failed';
                            }
                        }

                        // Method 5: Create debug record even if no data found
                        if (!scheduleData) {
                            logger.warn('⚠️ DEBUG: No schedule data found, creating debug record');
                            const workflowAny = workflow as any; // Type assertion for debug fallback
                            scheduleData = {
                                target: workflow.templateData?.target || workflowAny.target || '',
                                value: '0',
                                data: workflow.templateData?.data || workflowAny.data || '0x',
                                predecessor: '0x0000000000000000000000000000000000000000000000000000000000000000',
                                salt: workflow.templateData?.salt || workflowAny.salt || '',
                                delay: workflow.templateData?.delay || workflowAny.delay || ''
                            };
                            operationId = workflow.templateData?.operationId || workflowAny.operationId;
                            status = workflow.templateData?.status || workflowAny.status || 'unknown';
                            extractionMethod = 'debug_fallback';
                        }

                        logger.log('🔍 DEBUG: Final extraction result:', {
                            method: extractionMethod,
                            hasTarget: !!scheduleData.target,
                            hasSalt: !!scheduleData.salt,
                            hasOperationId: !!operationId,
                            status: status
                        });

                        // CRITICAL FIX: Create tier1 object for frontend filtering compatibility
                        let tier1Object = null;

                        // If backend returned flattened tier1 data, reconstruct the tier1 object
                        if (extractionMethod === 'api_flattened_tier1_data') {
                            const workflowAny = workflow as any;
                            tier1Object = {
                                status: status,
                                operationId: operationId || '',
                                scheduleParameters: scheduleData,
                                workflowName: workflowAny.workflowName || workflow.name,
                                transactionHash: workflowAny.transactionHash || workflow.transactionHash,
                                lastStatusCheck: workflowAny.lastStatusCheck || Date.now(),
                                executionTimestamp: workflow.created || Date.now(),
                                multiSigAddress: workflowAny.multiSigAddress || '',
                                blockNumber: workflow.blockNumber || 0,
                                gasUsed: workflowAny.gasUsed || '0'
                            };
                        } else {
                            // Use existing tier1 object if available
                            tier1Object = (workflow as any).tier1 || null;
                        }

                        // CRITICAL FIX: Include ALL workflows for debugging, regardless of target
                        const completionData: WorkflowCompletionData = {
                            workflowId: workflow.id || 'unknown',
                            workflowName: workflow.name || 'Unknown Workflow',
                            transactionHash: workflow.transactionHash || workflow.templateData?.transactionHash || 'unknown',
                            blockNumber: workflow.blockNumber || workflow.templateData?.blockNumber || 0,
                            gasUsed: workflow.gasUsed || workflow.templateData?.gasUsed || '0',
                            scheduleData: scheduleData,
                            executionTimestamp: workflow.created || workflow.templateData?.executionTimestamp || Date.now(),
                            status: status as any,
                            multiSigAddress: workflow.templateData?.multiSigAddress || workflow.scheduleParameters?.scheduledBy || '',
                            functionName: 'executeTransactionWithChecks',
                            originalFunctionData: workflow.functionData || '0x',
                            operationId: operationId || '', // FIXED: Use empty string instead of undefined
                            lastStatusCheck: workflow.templateData?.lastStatusCheck || Date.now(),
                            // CRITICAL FIX: Create proper tier1 structure for frontend filtering
                            tier1: tier1Object,
                            debugInfo: {
                                extractionMethod: extractionMethod,
                                originalWorkflowKeys: Object.keys(workflow),
                                hasTemplateData: !!workflow.templateData,
                                hasFunctionData: !!workflow.functionData,
                                functionDataType: typeof workflow.functionData,
                                // CRITICAL DEBUG: Log tier1 preservation
                                originalTier1Exists: !!((workflow as any).tier1),
                                originalTier1Status: (workflow as any).tier1?.status,
                                finalTier1Exists: !!tier1Object,
                                finalStatus: status,
                                statusSource: extractionMethod
                            }
                        };

                        // CRITICAL DEBUG: Log the final data structure that will be sent to frontend
                        logger.log('🔍 CRITICAL DEBUG: Final completion data structure for frontend:', {
                            workflowName: completionData.workflowName,
                            rootStatus: completionData.status,
                            hasTier1: !!completionData.tier1,
                            tier1Status: completionData.tier1?.status,
                            tier1Keys: completionData.tier1 ? Object.keys(completionData.tier1) : [],
                            extractionMethod: extractionMethod,
                            willPassFilter: completionData.tier1?.status === 'scheduled' || completionData.status === 'scheduled'
                        });

                        workflows.push(completionData);
                        logger.log('✅ DEBUG: Added workflow to list:', {
                            name: workflow.name,
                            method: extractionMethod,
                            target: scheduleData.target || 'EMPTY',
                            salt: scheduleData.salt ? scheduleData.salt.substring(0, 20) + '...' : 'EMPTY',
                            operationId: operationId ? operationId.substring(0, 20) + '...' : 'MISSING'
                        });

                    } catch (conversionError) {
                        logger.error('❌ DEBUG: Failed to convert workflow:', workflow.name, conversionError);
                    }
                }

                logger.log('✅ DEBUG: Final converted workflows:', workflows.length, 'items');
                workflows.forEach((w, i) => {
                    logger.log(`✅ DEBUG: Final workflow ${i}:`, {
                        name: w.workflowName,
                        target: w.scheduleData.target,
                        salt: w.scheduleData.salt?.substring(0, 20) + '...',
                        hasValidData: !!(w.scheduleData.target && w.scheduleData.salt)
                    });
                });

                return workflows;
            }

            logger.log('⚠️ No workflows found in backend');
            return [];

        } catch (error) {
            logger.error('❌ Backend workflow loading failed:', error);
            throw error;
        }
    }

    /**
     * Create execute workflow data from completed schedule workflow
     */
    async createExecuteWorkflowData(scheduleWorkflowId: string): Promise<{
        target: string;
        value: string;
        data: string;
        predecessor: string;
        salt: string;
    } | null> {
        try {
            const scheduleData = await this.getScheduleDataForExecute(scheduleWorkflowId);

            if (!scheduleData) {
                logger.error('❌ No schedule data found for workflow:', scheduleWorkflowId);
                return null;
            }

            // Return the exact parameters needed for timelockController.execute
            const executeData = {
                target: scheduleData.target,
                value: scheduleData.value,
                data: scheduleData.data, // This is the KV reference to original function
                predecessor: scheduleData.predecessor,
                salt: scheduleData.salt
            };

            logger.log('✅ Created execute workflow data:', {
                target: executeData.target,
                dataReference: scheduleData.data.substring(0, 30) + '...',
                salt: executeData.salt
            });

            return executeData;

        } catch (error) {
            logger.error('❌ Failed to create execute workflow data:', error);
            return null;
        }
    }

    /**
     * Clear completion data (for cleanup)
     */
    clearCompletionData(workflowId: string): void {
        try {
            sessionStorage.removeItem(`completion_${workflowId}`);

            // Also clear any fallback data
            const sessionKeys = Object.keys(sessionStorage);
            const fallbackKeys = sessionKeys.filter(key =>
                key.includes(workflowId) && key.includes('completion')
            );

            fallbackKeys.forEach(key => {
                sessionStorage.removeItem(key);
            });

            logger.log('🧹 Cleared completion data for workflow:', workflowId);
        } catch (error) {
            logger.error('❌ Failed to clear completion data:', error);
        }
    }

    // REMOVED: createEnhancedScheduleParameters method - using simple scheduleParameters instead

    /**
     * Determine salt source based on capture method
     */
    private determineSaltSource(salt: string, captureMethod: string): 'generated' | 'user_input' | 'extracted' | 'session_storage' {
        if (captureMethod.includes('transaction execution context')) {
            return 'extracted';
        } else if (captureMethod.includes('sessionStorage')) {
            return 'session_storage';
        } else if (captureMethod.includes('generated')) {
            return 'generated';
        } else {
            return 'user_input';
        }
    }

    /**
     * Get salt description based on source
     */
    private getSaltDescription(salt: string, captureMethod: string): string {
        if (salt === '0' || salt === '') {
            return '(EMPTY - this is the issue!)';
        } else if (captureMethod.includes('transaction execution context')) {
            return '(extracted from transaction inputs)';
        } else if (captureMethod.includes('sessionStorage')) {
            return '(retrieved from session storage)';
        } else if (captureMethod.includes('generated')) {
            return '(generated by salt utility)';
        } else {
            return '(user provided)';
        }
    }

    /**
     * PHASE 2.1: Extract operationId from transaction logs using CallScheduled events
     */
    async extractOperationIdFromTransaction(
        transactionHash: string,
        provider: any
    ): Promise<{ operationId: string | null; extractionMethod: string }> {
        try {
            logger.log('🔍 PHASE 2: Extracting operationId from transaction:', transactionHash);

            // Get transaction receipt
            const receipt = await provider.getTransactionReceipt(transactionHash);
            if (!receipt) {
                logger.warn('⚠️ PHASE 2: No receipt found for transaction:', transactionHash);
                return { operationId: null, extractionMethod: 'no_receipt_found' };
            }

            logger.log('📋 PHASE 2: Transaction receipt found:', {
                blockNumber: receipt.blockNumber,
                logsCount: receipt.logs.length,
                status: receipt.status
            });

            // Look for CallScheduled event in logs
            // CallScheduled event signature: CallScheduled(bytes32 indexed id, uint256 indexed index, address target, uint256 value, bytes data, bytes32 predecessor, uint256 delay)
            const callScheduledTopic = '0x4cf4410cc57040e44862ef0f45f3dd5a5e02db8eb8add648d4b0e236f1d07dca'; // keccak256("CallScheduled(bytes32,uint256,address,uint256,bytes,bytes32,uint256)")

            for (const log of receipt.logs) {
                try {
                    // Check if this log matches CallScheduled event
                    if (log.topics && log.topics.length > 0 && log.topics[0] === callScheduledTopic) {
                        logger.log('✅ PHASE 2: Found CallScheduled event in log');

                        // The operationId is the first indexed parameter (topics[1])
                        const operationId = log.topics[1];

                        logger.log('🎯 PHASE 2: Extracted operationId from CallScheduled event:', {
                            operationId,
                            logIndex: log.logIndex,
                            blockNumber: log.blockNumber
                        });

                        return {
                            operationId,
                            extractionMethod: 'CallScheduled_event_log'
                        };
                    }
                } catch (logError) {
                    logger.warn('⚠️ PHASE 2: Error processing log:', logError);
                }
            }

            // Fallback: Try to compute operationId from transaction inputs
            logger.log('⚠️ PHASE 2: No CallScheduled event found, attempting fallback computation');

            try {
                const transaction = await provider.getTransaction(transactionHash);
                if (transaction && transaction.data) {
                    // This is a simplified fallback - in reality, we'd need to decode the transaction data
                    // and compute the operationId using the same algorithm as the timelock contract
                    logger.log('📄 PHASE 2: Transaction data available for fallback computation');

                    // For now, return null and indicate the method tried
                    return {
                        operationId: null,
                        extractionMethod: 'fallback_computation_attempted'
                    };
                }
            } catch (fallbackError) {
                logger.warn('⚠️ PHASE 2: Fallback computation failed:', fallbackError);
            }

            logger.warn('❌ PHASE 2: Could not extract operationId from transaction:', transactionHash);
            return {
                operationId: null,
                extractionMethod: 'extraction_failed'
            };

        } catch (error) {
            logger.error('❌ PHASE 2: Error extracting operationId:', error);
            return {
                operationId: null,
                extractionMethod: 'error_during_extraction'
            };
        }
    }

    /**
     * PHASE 2.2 & 2.5: Enhanced workflow completion storage with tier1 parameters and operationId
     */
    async storeEnhancedWorkflowCompletion(
        completionData: WorkflowCompletionData,
        provider?: any
    ): Promise<WorkflowCompletionResponse> {
        try {
            logger.log('💾 PHASE 2: Storing enhanced workflow completion with operationId extraction');

            // PHASE 2.1: Compute operationId using tier1 schedule parameters - ENHANCED
            let operationId: string | null = null;
            let operationIdExtractionMethod = 'not_attempted';

            // CRITICAL FIX: Check for pre-computed operationId from FunctionExplorer first
            const preComputedOperationId = sessionStorage.getItem('computedOperationId');
            if (preComputedOperationId) {
                logger.log('✅ OPERATIONID FIX: Using pre-computed operationId from FunctionExplorer:', preComputedOperationId.substring(0, 20) + '...');
                operationId = preComputedOperationId;
                operationIdExtractionMethod = 'pre_computed_with_decrypted_data';

                // Clean up the sessionStorage
                sessionStorage.removeItem('computedOperationId');
                logger.log('🧹 OPERATIONID FIX: Cleaned up pre-computed operationId from sessionStorage');
            } else {
                // Fallback to the original computation method (but this should not be used for KV references)
                const scheduleParams = completionData.scheduleData;
                if (scheduleParams && scheduleParams.target) {
                    try {
                        // CRITICAL FIX: Always attempt to compute operationId, even with empty/zero salt
                        const targetParam = scheduleParams.target;
                        const valueParam = scheduleParams.value || '0';
                        const dataParam = scheduleParams.data || '0x';
                        const predecessorParam = scheduleParams.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000';
                        const saltParam = scheduleParams.salt || '0x0000000000000000000000000000000000000000000000000000000000000000';

                        logger.log('🔍 PHASE 2: Attempting fallback operationId computation with parameters:', {
                            target: targetParam,
                            value: valueParam,
                            data: dataParam.substring(0, 20) + '...',
                            predecessor: predecessorParam,
                            salt: saltParam.substring(0, 20) + '...',
                            saltIsEmpty: !saltParam || saltParam === '' || saltParam === '0',
                            isKvReference: backendDataHandler.isSecureReference(dataParam)
                        });

                        // CRITICAL: Don't compute operationId if data is a KV reference
                        if (backendDataHandler.isSecureReference(dataParam)) {
                            logger.warn('⚠️ OPERATIONID FIX: Data parameter is KV reference - cannot compute operationId without decryption');
                            operationIdExtractionMethod = 'kv_reference_requires_decryption';
                        } else {
                            operationId = computeOperationId(
                                targetParam,
                                valueParam,
                                dataParam,
                                predecessorParam,
                                saltParam
                            );

                            if (scheduleParams.salt && scheduleParams.salt !== '' && scheduleParams.salt !== '0') {
                                operationIdExtractionMethod = 'computed_from_tier1_parameters';
                            } else {
                                operationIdExtractionMethod = 'computed_with_fallback_salt';
                            }

                            logger.log('🔍 PHASE 2: Fallback operationId computation result:', {
                                operationId: operationId ? operationId.substring(0, 20) + '...' : null,
                                method: operationIdExtractionMethod,
                                usedParameters: {
                                    target: targetParam,
                                    value: valueParam,
                                    salt: saltParam.substring(0, 20) + '...',
                                    saltWasEmpty: !scheduleParams.salt || scheduleParams.salt === '' || scheduleParams.salt === '0'
                                }
                            });
                        }
                    } catch (computeError) {
                        logger.error('❌ PHASE 2: Failed to compute operation ID:', computeError);
                        operationIdExtractionMethod = 'computation_failed';
                    }
                } else {
                    logger.warn('⚠️ PHASE 2: Missing target parameter for operation ID computation:', {
                        hasTarget: !!scheduleParams?.target,
                        hasScheduleParams: !!scheduleParams
                    });
                    operationIdExtractionMethod = 'missing_target_parameter';
                }
            }

            // PHASE 2: Enhance completion data with new fields
            const enhancedCompletionData: WorkflowCompletionData = {
                ...completionData,
                // PHASE 2: Add new tracking fields
                operationId: operationId || '', // FIXED: Use empty string instead of undefined
                lastStatusCheck: Date.now(),
                status: completionData.status, // FIXED: Keep original status, don't change to 'scheduled' until transaction execution
                kvReferences: this.extractKvReferences(completionData),
                debugInfo: {
                    ...completionData.debugInfo,
                    operationIdExtractionMethod,
                    scheduleParametersIncluded: true // NEW: using simplified scheduleParameters structure
                }
            };

            logger.log('🔍 PHASE 2: Status logic applied:', {
                hasOperationId: !!operationId,
                originalStatus: completionData.status,
                finalStatus: enhancedCompletionData.status,
                statusReason: 'OperationId computed but status remains unchanged until transaction execution'
            });

            logger.log('🔍 PHASE 2: Enhanced completion data structure:', {
                hasOperationId: !!enhancedCompletionData.operationId,
                status: enhancedCompletionData.status,
                kvReferencesCount: enhancedCompletionData.kvReferences?.length || 0,
                lastStatusCheck: new Date(enhancedCompletionData.lastStatusCheck || 0).toISOString()
            });

            // Generate unique completion ID
            const completionId = `${this.COMPLETION_PREFIX}${enhancedCompletionData.workflowId}_${Date.now()}`;

            // PHASE 2.2: Create tier1 structure with readable parameters
            const tier1Data = {
                workflowName: enhancedCompletionData.workflowName,
                transactionHash: enhancedCompletionData.transactionHash,
                operationId: enhancedCompletionData.operationId,
                status: enhancedCompletionData.status,
                lastStatusCheck: enhancedCompletionData.lastStatusCheck,
                scheduleParameters: {
                    target: enhancedCompletionData.scheduleData.target,
                    value: enhancedCompletionData.scheduleData.value,
                    data: enhancedCompletionData.scheduleData.data, // KV reference
                    predecessor: enhancedCompletionData.scheduleData.predecessor,
                    salt: enhancedCompletionData.scheduleData.salt, // PHASE 2.5: Real salt value
                    delay: enhancedCompletionData.scheduleData.delay // PHASE 2.5: Real delay value
                },
                kvReferences: enhancedCompletionData.kvReferences,
                executionTimestamp: enhancedCompletionData.executionTimestamp,
                multiSigAddress: enhancedCompletionData.multiSigAddress
            };

            // Store with tier1/tier2 structure
            const storageData = {
                tier1: tier1Data,
                tier2: enhancedCompletionData // Full data in encrypted tier2
            };

            logger.log('🔍 PHASE 2: Tier1 data structure:', {
                hasOperationId: !!tier1Data.operationId,
                saltValue: tier1Data.scheduleParameters.salt ? tier1Data.scheduleParameters.salt.substring(0, 20) + '...' : 'MISSING',
                delayValue: tier1Data.scheduleParameters.delay,
                status: tier1Data.status
            });

            // CRITICAL FIX: Store tier1 data directly in WorkflowData fields to bypass backend tier1/tier2 wrapping
            // The backend API automatically wraps everything with tier1/tier2, so we need to put our tier1 data
            // in the main WorkflowData structure where it won't get overridden
            const workflowDataWithTier1 = {
                id: completionId,
                name: tier1Data.workflowName,
                description: `Workflow completion record for ${tier1Data.workflowName}`,
                category: 'utility' as const,
                difficulty: 'intermediate' as const,
                estimatedTime: 5,
                tags: ['workflow', 'completion', tier1Data.status],
                created: Date.now(),

                // CRITICAL: Store tier1 data directly in main fields to avoid backend override
                functionData: JSON.stringify(storageData), // Backup: Our tier1/tier2 structure as JSON
                templateData: {
                    // TIER1 DATA DIRECTLY ACCESSIBLE - This is what we want in the final JSON
                    workflowName: tier1Data.workflowName,
                    transactionHash: tier1Data.transactionHash,
                    operationId: tier1Data.operationId,
                    status: tier1Data.status,
                    lastStatusCheck: tier1Data.lastStatusCheck,
                    scheduleParameters: {
                        target: tier1Data.scheduleParameters.target,
                        value: tier1Data.scheduleParameters.value,
                        data: tier1Data.scheduleParameters.data, // KV reference
                        predecessor: tier1Data.scheduleParameters.predecessor,
                        salt: tier1Data.scheduleParameters.salt, // ACTUAL SALT
                        delay: tier1Data.scheduleParameters.delay // ACTUAL DELAY
                    },
                    kvReferences: tier1Data.kvReferences,
                    executionTimestamp: tier1Data.executionTimestamp,
                    multiSigAddress: tier1Data.multiSigAddress,
                    // FULL TIER2 DATA
                    fullWorkflowData: enhancedCompletionData
                },

                // Also store key fields directly for easy backend filtering
                transactionHash: tier1Data.transactionHash,
                blockNumber: enhancedCompletionData.blockNumber,
                gasUsed: enhancedCompletionData.gasUsed,
                scheduleParameters: tier1Data.scheduleParameters, // Direct access to schedule params
                workflowCompletionId: completionId
            };

            logger.log('🔍 CRITICAL FIX: Storing workflow data with tier1 in templateData:', {
                hasTemplateData: !!workflowDataWithTier1.templateData,
                hasScheduleParameters: !!workflowDataWithTier1.templateData.scheduleParameters,
                saltValue: workflowDataWithTier1.templateData.scheduleParameters?.salt?.substring(0, 20) + '...',
                delayValue: workflowDataWithTier1.templateData.scheduleParameters?.delay
            });

            // CRITICAL DEBUG: Log the exact data structure being sent to API
            logger.log('🔍 DEBUG: Exact data structure being sent to API:', {
                id: workflowDataWithTier1.id,
                name: workflowDataWithTier1.name,
                hasTemplateData: !!workflowDataWithTier1.templateData,
                templateDataKeys: workflowDataWithTier1.templateData ? Object.keys(workflowDataWithTier1.templateData) : [],
                hasScheduleParameters: !!(workflowDataWithTier1.templateData && workflowDataWithTier1.templateData.scheduleParameters),
                scheduleParametersKeys: workflowDataWithTier1.templateData?.scheduleParameters ? Object.keys(workflowDataWithTier1.templateData.scheduleParameters) : [],
                saltValue: workflowDataWithTier1.templateData?.scheduleParameters?.salt,
                targetValue: workflowDataWithTier1.templateData?.scheduleParameters?.target,
                dataStructurePreview: JSON.stringify(workflowDataWithTier1).substring(0, 200) + '...'
            });

            const response = await workflowStorageService.storeWorkflow(workflowDataWithTier1, completionId);

            logger.log('🔍 PHASE 2: workflowStorageService response structure:', {
                success: response.success,
                hasData: !!response.data,
                hasId: !!response.id,
                dataId: response.data?.id,
                directId: response.id,
                fullResponse: response
            });

            if (response.success) {
                // CRITICAL FIX: Handle different response structures
                const responseId = response.id || response.data?.id || completionId;

                logger.log('✅ PHASE 2: Enhanced workflow completion stored successfully:', responseId);

                // Also store in sessionStorage for immediate access
                sessionStorage.setItem(
                    `completion_${enhancedCompletionData.workflowId}`,
                    JSON.stringify(enhancedCompletionData)
                );

                return {
                    success: true,
                    id: responseId
                };
            } else {
                throw new Error(response.error || 'Failed to store enhanced completion data');
            }

        } catch (error) {
            logger.error('❌ PHASE 2: Failed to store enhanced workflow completion:', error);

            // Fallback to enhanced method (recursive call with provider=undefined)
            return await this.storeEnhancedWorkflowCompletion(completionData);
        }
    }

    /**
     * PHASE 2.6: Extract KV references from completion data
     */
    private extractKvReferences(completionData: WorkflowCompletionData): string[] {
        const kvReferences: string[] = [];

        // Extract from scheduleData.data (KV reference to original function)
        if (completionData.scheduleData.data && backendDataHandler.isSecureReference(completionData.scheduleData.data)) {
            kvReferences.push(completionData.scheduleData.data);
        }

        // Extract from originalFunctionData
        if (completionData.originalFunctionData && backendDataHandler.isSecureReference(completionData.originalFunctionData)) {
            kvReferences.push(completionData.originalFunctionData);
        }

        // Extract from debugInfo if available
        if (completionData.debugInfo?.kvReferences) {
            kvReferences.push(...completionData.debugInfo.kvReferences);
        }

        // Remove duplicates
        return [...new Set(kvReferences)];
    }


    /**
     * Health check
     */
    async healthCheck(): Promise<boolean> {
        try {
            return await backendDataHandler.healthCheck();
        } catch (error) {
            logger.error('Completion service health check failed:', error);
            return false;
        }
    }
}

// Export singleton instance
export const workflowCompletionService = new WorkflowCompletionService();
export default workflowCompletionService;
